import axios from "axios";
import { apiUrl, logApiError } from "@/shared/services/config.js";
import { fmtDate } from "@/shared/utils/dateHelpers.js";
import {
  SpecialityQuestionGroupDto,
  SubmissionQuestionAnswersSaveRequest,
  SubmissionQuestionGroupResponseDto,
} from "@/shared/dtos/index.js";

// Per-tab cache — the master tree rarely changes, so we memoize per
// speciality id. We also dedupe in-flight requests via `inflight` so
// two simultaneous callers (e.g. bootstrap + page hook) share one
// network round-trip.
const questionsCacheBySpec = new Map();
const inflightBySpec = new Map();

/**
 * Master question tree for a speciality. Cached per tab in a Map — one
 * network call per (specialityId) per tab. Response is wrapped in
 * SpecialityQuestionGroupDto instances so every consumer reads a tree
 * of class instances with safe defaults.
 *
 * @param {string|number} specialityId
 * @returns {Promise<SpecialityQuestionGroupDto[]>}
 */
export const fetchQuestionsBySpeciality = async (specialityId) => {
  const key = String(specialityId);
  if (questionsCacheBySpec.has(key)) return questionsCacheBySpec.get(key);
  if (inflightBySpec.has(key)) return inflightBySpec.get(key);
  const p = (async () => {
    try {
      const response = await axios.get(
        apiUrl("/questions"),
        { params: { specialityId: key }, withCredentials: true },
      );
      const dtos = Array.isArray(response.data)
        ? response.data.map((g) => new SpecialityQuestionGroupDto(g))
        : [];
      questionsCacheBySpec.set(key, dtos);
      return dtos;
    } catch (error) {
      logApiError(error);
      throw error;
    } finally {
      inflightBySpec.delete(key);
    }
  })();
  inflightBySpec.set(key, p);
  return p;
};

/**
 * GET /questions/{submissionId}/questions — submission-scoped tree (carries
 * saved answers).
 * @param {string|number} submissionId
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<SubmissionQuestionGroupResponseDto[]>}
 */
export const fetchSubmissionQuestions = async (submissionId, { signal } = {}) => {
  try {
    const response = await axios.get(
      apiUrl(`/questions/${submissionId}/questions`),
      { withCredentials: true, signal },
    );
    return Array.isArray(response.data)
      ? response.data.map((g) => new SubmissionQuestionGroupResponseDto(g))
      : [];
  } catch (error) {
    logApiError(error);
    throw error;
  }
};

/**
 * POST /questions/save — persist answers for one or more groups.
 * @param {Partial<SubmissionQuestionAnswersSaveRequest>} payload
 * @returns {Promise<boolean>}
 */
export const saveSubmissionQuestionAnswers = async (payload) => {
  try {
    const response = await axios.post(
      apiUrl("/questions/save"),
      new SubmissionQuestionAnswersSaveRequest(payload),
      { withCredentials: true },
    );
    return response.data === true;
  } catch (error) {
    logApiError(error);
    throw error;
  }
};

export function buildOptionImpactsPayload(option, impactAnswers) {
  const impacts = Array.isArray(option?.impacts) ? option.impacts : [];
  if (impacts.length === 0) return [];
  return impacts.map((imp) => {
    const userVal = impactAnswers?.[String(imp.id)];
    const inputType = String(imp?.impactInputType || "").toUpperCase();
    const isDate = inputType === "DATE_PICKER" || inputType === "DATE";
    let answerValue;
    if (userVal != null && userVal !== "") {
      answerValue = isDate ? fmtDate(String(userVal)) : String(userVal);
    } else {
      answerValue = imp?.answerValue == null ? "" : String(imp.answerValue);
      if (isDate && answerValue !== "") answerValue = fmtDate(answerValue) || answerValue;
    }
    return { submissionQuestionImpactId: imp.id, answerValue };
  });
}

export function buildSingleGroupSavePayload(groupRegex, submissionId, submissionGroups, answers, impactAnswers, hiddenQuestionIds) {
  if (!Array.isArray(submissionGroups)) return null;
  const subGroup = submissionGroups.find((g) => groupRegex.test(String(g?.groupName || "")));
  if (!subGroup) return null;

  const questions = [];
  for (const subQ of subGroup.questions || []) {
    // Skip a stale answer for a now-hidden question (FR4): a hidden question
    // must never contribute a value to the outgoing save, even if a prior
    // answer still lingers in the map.
    if (hiddenQuestionIds instanceof Set && hiddenQuestionIds.has(subQ.id)) continue;
    const qid = String(subQ.id);
    const ans = answers[qid];
    if (ans == null) continue;

    const options = [];

    if (subQ.questionType === "TEXTBOX") {
      const text = String(ans);
      if (text.trim() !== "") {
        const opt = (subQ.options || [])[0];
        if (opt) {
          options.push({
            submissionQuestionOptionId: opt.id,
            answerValue: text,
            impacts: buildOptionImpactsPayload(opt, impactAnswers),
          });
        }
      }
    } else if (subQ.questionType === "CHECKBOX") {
      const ids = Array.isArray(ans) ? ans : [];
      for (const id of ids) {
        const opt = (subQ.options || []).find((o) => String(o.id) === id);
        if (opt) {
          options.push({
            submissionQuestionOptionId: opt.id,
            answerValue: String(opt.optionValue ?? opt.optionLabel ?? ""),
            impacts: buildOptionImpactsPayload(opt, impactAnswers),
          });
        }
      }
    } else {
      const opt = (subQ.options || []).find((o) => String(o.id) === String(ans));
      if (opt) {
        options.push({
          submissionQuestionOptionId: opt.id,
          answerValue: String(opt.optionValue ?? opt.optionLabel ?? ""),
          impacts: buildOptionImpactsPayload(opt, impactAnswers),
        });
      }
    }

    if (options.length > 0) {
      questions.push({ submissionQuestionId: subQ.id, options });
    }
  }

  if (questions.length === 0) return null;
  return {
    submissionId: Number(submissionId),
    groups: [{ submissionQuestionGroupId: subGroup.id, questions }],
  };
}

export function buildCoverageGroupSavePayload(submissionId, submissionGroups, coverageAnswers, impactAnswers, hiddenQuestionIds) {
  return buildSingleGroupSavePayload(/coverage\s*options/i, submissionId, submissionGroups, coverageAnswers, impactAnswers, hiddenQuestionIds);
}

export function buildUnderwritingGroupSavePayload(submissionId, submissionGroups, underwritingAnswers, impactAnswers, hiddenQuestionIds) {
  return buildSingleGroupSavePayload(/underwriting/i, submissionId, submissionGroups, underwritingAnswers, impactAnswers, hiddenQuestionIds);
}

export function buildSignupQuestionSaveRequest(questionGroups, questionAnswers, impactAnswers, hiddenQuestionIds) {
  if (!Array.isArray(questionGroups) || questionGroups.length === 0) return null;
  const groups = [];
  for (const group of questionGroups) {
    if (!group?.id || !Array.isArray(group.questions)) continue;
    const questions = [];
    for (const q of group.questions) {
      // Skip a now-hidden question's stale answer (FR4) — see the same guard
      // in buildSingleGroupSavePayload.
      if (hiddenQuestionIds instanceof Set && hiddenQuestionIds.has(q.id)) continue;
      const ans = questionAnswers?.[String(q.id)];
      if (ans == null) continue;
      const options = [];
      const qType = String(q.questionType || "").toUpperCase();
      if (qType === "TEXTBOX" || qType === "TEXT_AREA" || qType === "NUMBER_INPUT" || qType === "DATE_PICKER") {
        const text = String(ans);
        if (text.trim()) {
          const opt = Array.isArray(q.options) && q.options.length > 0 ? q.options[0] : null;
          if (opt) {
            options.push({
              submissionQuestionOptionId: opt.id,
              answerValue: text,
              impacts: buildOptionImpactsPayload(opt, impactAnswers),
            });
          }
        }
      } else if (qType === "CHECKBOX") {
        const ids = Array.isArray(ans) ? ans : [];
        for (const id of ids) {
          const opt = (q.options || []).find((o) => String(o.id) === String(id));
          if (opt) {
            options.push({
              submissionQuestionOptionId: opt.id,
              answerValue: String(opt.optionValue ?? opt.optionLabel ?? ""),
              impacts: buildOptionImpactsPayload(opt, impactAnswers),
            });
          }
        }
      } else {
        const opt = (q.options || []).find((o) => String(o.id) === String(ans));
        if (opt) {
          options.push({
            submissionQuestionOptionId: opt.id,
            answerValue: String(opt.optionValue ?? opt.optionLabel ?? ""),
            impacts: buildOptionImpactsPayload(opt, impactAnswers),
          });
        }
      }
      if (options.length > 0) {
        questions.push({ submissionQuestionId: q.id, options });
      }
    }
    groups.push({ submissionQuestionGroupId: group.id, questions });
  }
  return groups.length > 0 ? { groups } : null;
}

// ── Display order + Visibility (Hide/Show) resolution ────────────────────
// Pure helpers (no HTTP, no React, no store). The submission tree and the
// master/pre-submission tree share every function here except the two
// resolvers (resolveVisibilityTargets vs resolveMasterVisibilityTargets),
// which differ only in whether the raw hide/show ids need remapping to
// submission-scoped ids. See the Q2B "Question Visibility & Display Order"
// handover for the full rationale.

/**
 * Non-mutating sort by ascending `displayOrder` (missing → 0). Relies on a
 * stable sort for tie-breaking. Exported three times under intent-revealing
 * names because groups, questions, and options all share the shape.
 */
function sortedByDisplayOrder(list) {
  if (!Array.isArray(list)) return [];
  return [...list].sort((a, b) => (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0));
}
export const sortedGroups = sortedByDisplayOrder;
export const sortedQuestions = sortedByDisplayOrder;
export const sortedOptions = sortedByDisplayOrder;

/**
 * Shallow-merge the page-scoped answer maps (question / coverage /
 * underwriting) into one question-id-keyed map. Key spaces don't collide —
 * each question belongs to exactly one group.
 */
export function mergeAnswerMaps(...maps) {
  return Object.assign({}, ...maps.filter((m) => m && typeof m === "object"));
}

/**
 * Current answer for a question, for hidden-set purposes: a live in-progress
 * answer wins (so hide/show fires immediately on the edited page); otherwise
 * fall back to the persisted `option.answerValue` already on the tree (so
 * cross-group cascades resolve without waiting on another page's prefill).
 */
export function effectiveAnswerForQuestion(question, liveAnswersByQuestionId) {
  const qid = String(question?.id);
  if (liveAnswersByQuestionId && Object.prototype.hasOwnProperty.call(liveAnswersByQuestionId, qid)) {
    return liveAnswersByQuestionId[qid];
  }
  const opts = Array.isArray(question?.options) ? question.options : [];
  const answered = opts.filter((o) => o?.answerValue != null && String(o.answerValue).trim() !== "");
  if (answered.length === 0) return undefined;
  const qType = String(question?.questionType || "").toUpperCase();
  if (qType === "CHECKBOX") return answered.map((o) => String(o.id));
  if (qType === "TEXTBOX" || qType === "TEXT_AREA" || qType === "NUMBER_INPUT" || qType === "DATE_PICKER") {
    return String(answered[0].answerValue);
  }
  return String(answered[0].id);
}

/**
 * Submission-tree resolver. Input: the full submission tree (needed for
 * cross-group rules). Output: a new tree where every option additionally
 * carries `visibilityHideTargetIds`/`visibilityShowTargetIds` (submission-
 * scoped question ids, resolved from the raw master-id strings via a
 * Map<String(masterQuestionId), submissionQuestionId>), plus the structural
 * `showTargetQuestionIds` Set (every question that is any Show rule's target).
 */
export function resolveVisibilityTargets(submissionGroups) {
  const groups = Array.isArray(submissionGroups) ? submissionGroups : [];
  const submissionIdByMasterQuestionId = new Map();
  for (const g of groups) {
    for (const q of g?.questions || []) {
      if (q?.questionId != null) submissionIdByMasterQuestionId.set(String(q.questionId), q.id);
    }
  }
  const showTargetQuestionIds = new Set();
  const resolveIds = (masterIds) => {
    const out = [];
    for (const masterId of Array.isArray(masterIds) ? masterIds : []) {
      const resolved = submissionIdByMasterQuestionId.get(String(masterId));
      if (resolved != null) out.push(resolved);
    }
    return out;
  };
  const augmentedGroups = groups.map((g) => ({
    ...g,
    questions: (g?.questions || []).map((q) => ({
      ...q,
      options: (q?.options || []).map((o) => {
        const visibilityHideTargetIds = resolveIds(o?.hideQuestions);
        const visibilityShowTargetIds = resolveIds(o?.showQuestions);
        for (const id of visibilityShowTargetIds) showTargetQuestionIds.add(id);
        return { ...o, visibilityHideTargetIds, visibilityShowTargetIds };
      }),
    })),
  }));
  return { groups: augmentedGroups, showTargetQuestionIds };
}

/**
 * Master-tree counterpart of resolveVisibilityTargets. Same output shape, but
 * NO id-remapping step: on the master tree a question's own `id` already IS
 * the master `question.id` that hideQuestions/showQuestions reference, so the
 * raw ids are coerced straight to Number and used as-is.
 */
export function resolveMasterVisibilityTargets(questionGroups) {
  const groups = Array.isArray(questionGroups) ? questionGroups : [];
  const showTargetQuestionIds = new Set();
  const toIds = (rawIds) =>
    (Array.isArray(rawIds) ? rawIds : [])
      .map((id) => Number(id))
      .filter((n) => !Number.isNaN(n));
  const augmentedGroups = groups.map((g) => ({
    ...g,
    questions: (g?.questions || []).map((q) => ({
      ...q,
      options: (q?.options || []).map((o) => {
        const visibilityHideTargetIds = toIds(o?.hideQuestions);
        const visibilityShowTargetIds = toIds(o?.showQuestions);
        for (const id of visibilityShowTargetIds) showTargetQuestionIds.add(id);
        return { ...o, visibilityHideTargetIds, visibilityShowTargetIds };
      }),
    })),
  }));
  return { groups: augmentedGroups, showTargetQuestionIds };
}

/** Single predicate reused by every page + derivedValues + the recompute. */
export function isQuestionVisible(question, hiddenQuestionIds) {
  return !(hiddenQuestionIds instanceof Set && hiddenQuestionIds.has(question?.id));
}

/**
 * The option(s) currently "active" for a question given its effective answer.
 * Free-text types never have an active option — Visibility only ever attaches
 * to a choice option — so they're excluded from firing any Hide/Show rule.
 */
function activeOptionsForQuestion(question, effectiveAnswer) {
  if (effectiveAnswer == null) return [];
  const qType = String(question?.questionType || "").toUpperCase();
  const opts = Array.isArray(question?.options) ? question.options : [];
  if (qType === "TEXTBOX" || qType === "TEXT_AREA" || qType === "NUMBER_INPUT" || qType === "DATE_PICKER") return [];
  if (qType === "CHECKBOX") {
    const ids = Array.isArray(effectiveAnswer) ? effectiveAnswer.map(String) : [];
    return opts.filter((o) => ids.includes(String(o.id)));
  }
  const selected = opts.find((o) => String(o.id) === String(effectiveAnswer));
  return selected ? [selected] : [];
}

/**
 * Core algorithm. Returns a NEW Set<number> (never mutates). Must be called on
 * the AUGMENTED tree from resolveVisibilityTargets / resolveMasterVisibilityTargets
 * (needs visibilityHideTargetIds/visibilityShowTargetIds on each option).
 *
 * 1. Seed with `showTargetQuestionIds` — every Show target starts hidden.
 * 2. Walk groups then questions in (group.displayOrder, question.displayOrder)
 *    order, single pass.
 * 3. Skip an already-hidden question as a source (it can't hide/show anything).
 * 4. For each active option, add hide targets / remove show targets.
 * 5. OR semantics fall out; later-walked sources win on conflict.
 */
export function recomputeHiddenQuestionIds(submissionGroups, liveAnswersByQuestionId, showTargetQuestionIds) {
  const hidden = new Set(showTargetQuestionIds instanceof Set ? showTargetQuestionIds : []);
  const groups = sortedGroups(submissionGroups);
  for (const g of groups) {
    const questions = sortedQuestions(g?.questions || []);
    for (const q of questions) {
      if (hidden.has(q.id)) continue; // a hidden question can't itself hide/show anything
      const effectiveAnswer = effectiveAnswerForQuestion(q, liveAnswersByQuestionId);
      for (const opt of activeOptionsForQuestion(q, effectiveAnswer)) {
        for (const id of opt.visibilityHideTargetIds || []) hidden.add(id);
        for (const id of opt.visibilityShowTargetIds || []) hidden.delete(id);
      }
    }
  }
  return hidden;
}

/**
 * True when a single answer value counts as "provided": a non-blank string
 * (radio option id / textbox text) or a non-empty array (checkbox selections).
 */
function isAnswerProvided(ans) {
  if (ans == null) return false;
  if (Array.isArray(ans)) return ans.length > 0;
  return String(ans).trim() !== "";
}

/**
 * Continue-gate helper: returns true only when every *required*, currently
 * visible question in `questions` has a non-empty answer (and, when
 * `checkImpacts` is set, every required follow-up impact on the selected
 * option has a value). Pages call this from `canAdvance` so the Continue
 * button stays disabled until required questions are answered.
 *
 * Works for both master (`SpecialityQuestionRuleDto`) and submission
 * (`SubmissionQuestionResponseDto`) trees — both expose `id`, `isRequired`,
 * `questionType`, and `options`.
 *
 * @param {Array}  questions  question rows
 * @param {Record<string, string|string[]>} answers  keyed by question id
 * @param {object} [opts]
 * @param {(q:any)=>boolean} [opts.isVisible]  exclude conditionally-hidden
 *        questions (a hidden required question must never block Continue).
 * @param {Record<string,string>} [opts.impactAnswers]  keyed by impact id
 * @param {boolean} [opts.checkImpacts]  also require required, collect-from-UI
 *        impacts on the selected option(s) to be non-empty (default false).
 * @returns {boolean}
 */
export function requiredQuestionsAnswered(questions, answers, opts = {}) {
  const { isVisible = () => true, impactAnswers = {}, checkImpacts = false } = opts;
  if (!Array.isArray(questions)) return true;
  for (const q of questions) {
    if (!isVisible(q)) continue;
    const ans = answers?.[String(q.id)];
    if (q?.isRequired && !isAnswerProvided(ans)) return false;
    if (!checkImpacts || !isAnswerProvided(ans)) continue;

    // Required follow-up impacts hang off the selected option(s).
    const selectedIds = Array.isArray(ans)
      ? ans.map(String)
      : (String(q?.questionType).toUpperCase() === "TEXTBOX" ? [] : [String(ans)]);
    for (const opt of q?.options || []) {
      if (!selectedIds.includes(String(opt.id))) continue;
      for (const imp of opt.impacts || []) {
        // Submission DTOs omit `collectFromUi`; treat only an explicit false
        // as "not collected here" so required impacts still gate.
        if (imp?.isRequired && imp?.collectFromUi !== false) {
          const v = impactAnswers?.[String(imp.id)];
          if (v == null || String(v).trim() === "") return false;
        }
      }
    }
  }
  return true;
}

/** Find a submission group by name regex (matches the master group). */
export function findSubmissionGroup(submissionGroups, regex) {
  if (!Array.isArray(submissionGroups)) return null;
  return submissionGroups.find((g) => regex.test(String(g?.groupName || ""))) || null;
}

/**
 * Hydrate answers + impacts directly from a submission-scoped group. Used
 * by pages (Coverage / Underwriting) that render questions straight from
 * the submission tree, so answers are keyed by the submission question id
 * (and the option id) — no master-to-submission re-mapping needed.
 *
 * Seeds `isDefault` options when the user has never answered a question.
 *
 * @returns {{ answers: Record<string, string|string[]>, impacts: Record<string, string> }}
 */
export function hydrateSubmissionAnswers(subGroup) {
  const answers = {};
  const impacts = {};
  if (!subGroup) return { answers, impacts };

  for (const q of subGroup.questions || []) {
    const qid = String(q.id);
    const qType = String(q.questionType || "").toUpperCase();
    const opts = Array.isArray(q.options) ? q.options : [];

    const answered = opts.filter(
      (o) => o?.answerValue != null && String(o.answerValue).trim() !== "",
    );

    if (answered.length > 0) {
      if (qType === "TEXTBOX" || qType === "TEXT_AREA" || qType === "NUMBER_INPUT" || qType === "DATE_PICKER") {
        answers[qid] = String(answered[0].answerValue);
      } else if (qType === "CHECKBOX") {
        answers[qid] = answered.map((o) => String(o.id));
      } else {
        answers[qid] = String(answered[0].id);
      }
      for (const opt of answered) {
        for (const imp of opt.impacts || []) {
          if (imp?.answerValue != null && String(imp.answerValue).trim() !== "") {
            impacts[String(imp.id)] = String(imp.answerValue);
          }
        }
      }
      continue;
    }

    // No saved answer — seed the option flagged as default, if any.
    const def = opts.find((o) => o?.isDefault);
    if (!def) continue;
    if (qType === "CHECKBOX") answers[qid] = [String(def.id)];
    else if (qType !== "TEXTBOX" && qType !== "TEXT_AREA" && qType !== "NUMBER_INPUT" && qType !== "DATE_PICKER") {
      answers[qid] = String(def.id);
    }
  }

  return { answers, impacts };
}

/**
 * In a submission-scoped single-select question (RADIO_BUTTON / YES_NO), the
 * chosen option is the one the API ships back carrying a non-empty
 * `answerValue`. Returns that option, or null when the question is unanswered.
 */
function selectedSubmissionOption(question) {
  return (question?.options || []).find(
    (o) => o?.answerValue != null && String(o.answerValue).trim() !== "",
  ) || null;
}

/**
 * PA classification ("PA1" / "PA2") read straight from the submission tree's
 * "About your practice" → "PA classification" answer (an option whose value
 * mentions "obstet" means PA2). Returns null when the group/question/answer
 * isn't present yet, so callers can fall back to wizard-store state.
 */
export function classificationFromSubmissionGroups(submissionGroups) {
  const aboutGroup = findSubmissionGroup(submissionGroups, /about\s*your\s*practice/i);
  const q = (aboutGroup?.questions || []).find(
    (qq) => /pa\s*classification/i.test(String(qq?.questionText || "")),
  );
  const opt = selectedSubmissionOption(q);
  if (!opt) return null;
  return /obstet/i.test(String(opt.optionValue || opt.optionLabel || "")) ? "PA2" : "PA1";
}

/**
 * Prior-acts answer from the submission tree's "Coverage options" → "Prior
 * acts" question. Returns `{ isYes, retro }` (retro pulled from the selected
 * option's first non-empty date impact, if any), or null when unanswered.
 */
export function priorActsFromSubmissionGroups(submissionGroups) {
  const coverageGroup = findSubmissionGroup(submissionGroups, /coverage\s*options/i);
  const q = (coverageGroup?.questions || []).find(
    (qq) => /prior\s*acts/i.test(String(qq?.questionText || "")),
  );
  const opt = selectedSubmissionOption(q);
  if (!opt) return null;
  const isYes = /^yes/i.test(String(opt.optionValue || opt.optionLabel || ""));
  let retro = "";
  for (const imp of opt.impacts || []) {
    if (imp?.answerValue != null && String(imp.answerValue).trim() !== "") {
      retro = String(imp.answerValue);
      break;
    }
  }
  return { isYes, retro };
}

/**
 * Whether any underwriting question was answered "Yes" — read from the
 * submission tree's "Underwriting" group answers. Returns null when the group
 * has no yes/no questions to evaluate (tree not loaded yet), so callers can
 * fall back to wizard-store state.
 */
export function underwritingAnyYesFromSubmissionGroups(submissionGroups) {
  const group = findSubmissionGroup(submissionGroups, /underwriting/i);
  const questions = (group?.questions || []).filter(
    (q) => q?.questionType === "YES_NO" || q?.questionType === "RADIO_BUTTON",
  );
  if (questions.length === 0) return null;
  return questions.some((q) => {
    const opt = selectedSubmissionOption(q);
    return Boolean(opt) && /^yes/i.test(String(opt.optionValue || opt.optionLabel || ""));
  });
}

export function buildAboutGroupSavePayload(submissionId, submissionGroups, masterAboutGroup, answers) {
  if (!Array.isArray(submissionGroups) || !masterAboutGroup) return null;
  const norm = (s) => String(s || "").trim().toLowerCase();
  const subGroup = submissionGroups.find((g) => /about\s*your\s*practice/i.test(g?.groupName || ""));
  if (!subGroup) return null;

  const questions = [];
  for (const masterQ of masterAboutGroup.questions || []) {
    const qid = String(masterQ.id);
    const ans = answers[qid];
    if (ans == null) continue;

    const subQ = (subGroup.questions || []).find(
      (sq) => norm(sq.questionText) === norm(masterQ.questionText),
    );
    if (!subQ) continue;

    const options = [];

    if (masterQ.questionType === "TEXTBOX") {
      const text = String(ans);
      if (text.trim() !== "") {
        const subOpt = (subQ.options || [])[0];
        if (subOpt) options.push({ submissionQuestionOptionId: subOpt.id, answerValue: text });
      }
    } else if (masterQ.questionType === "CHECKBOX") {
      const selectedIds = Array.isArray(ans) ? ans : [];
      for (const mid of selectedIds) {
        const masterOpt = (masterQ.options || []).find((o) => String(o.id) === String(mid));
        if (!masterOpt) continue;
        const subOpt = (subQ.options || []).find(
          (so) => norm(so.optionLabel) === norm(masterOpt.optionLabel),
        );
        if (subOpt) {
          options.push({
            submissionQuestionOptionId: subOpt.id,
            answerValue: String(masterOpt.optionValue ?? masterOpt.optionLabel ?? ""),
          });
        }
      }
    } else {
      const masterOpt = (masterQ.options || []).find((o) => String(o.id) === String(ans));
      if (masterOpt) {
        const subOpt = (subQ.options || []).find(
          (so) => norm(so.optionLabel) === norm(masterOpt.optionLabel),
        );
        if (subOpt) {
          options.push({
            submissionQuestionOptionId: subOpt.id,
            answerValue: String(masterOpt.optionValue ?? masterOpt.optionLabel ?? ""),
          });
        }
      }
    }

    if (options.length > 0) {
      questions.push({ submissionQuestionId: subQ.id, options });
    }
  }

  if (questions.length === 0) return null;
  return {
    submissionId: Number(submissionId),
    groups: [{ submissionQuestionGroupId: subGroup.id, questions }],
  };
}
